import React, { useState, useEffect } from 'react'
import { 
  Save, 
  Building, 
  Hash, 
  FileText, 
  Settings,
  Upload,
  Download,
  RefreshCw
} from 'lucide-react'

interface ConfiguracaoEmpresa {
  razao_social: string
  nome_fantasia: string
  cnpj: string
  endereco: string
  cidade: string
  uf: string
  cep: string
  telefone: string
  email: string
  representante_legal: string
  cargo_representante: string
}

interface ConfiguracaoNumeracao {
  formato: string
  proximo_numero: number
  prefixo: string
  ano_atual: number
  reset_anual: boolean
}

interface ConfiguracaoPDF {
  margem_superior: number
  margem_inferior: number
  margem_esquerda: number
  margem_direita: number
  fonte_padrao: string
  tamanho_fonte: number
  incluir_cabecalho: boolean
  incluir_rodape: boolean
  watermark: string
}

const ContratosConfiguracoes: React.FC = () => {
  const [activeSection, setActiveSection] = useState('empresa')
  const [saving, setSaving] = useState(false)
  
  const [configEmpresa, setConfigEmpresa] = useState<ConfiguracaoEmpresa>({
    razao_social: 'M4 SOLUÇÕES E MARKETING VENDAS E CONSULTORIAS LTDA',
    nome_fantasia: 'M4 Digital',
    cnpj: '50.748.001/0001-35',
    endereco: 'Av. Imperador Pedro II, 352 Bairro Nova Petrópolis',
    cidade: 'São Bernardo do Campo',
    uf: 'SP',
    cep: '09770-420',
    telefone: '(11) 99999-9999',
    email: 'contato@m4.digital',
    representante_legal: 'Márcio Rogério Battistin Júnior',
    cargo_representante: 'CEO'
  })

  const [configNumeracao, setConfigNumeracao] = useState<ConfiguracaoNumeracao>({
    formato: 'M4-YYYY-XXX',
    proximo_numero: 1,
    prefixo: 'M4',
    ano_atual: new Date().getFullYear(),
    reset_anual: true
  })

  const [configPDF, setConfigPDF] = useState<ConfiguracaoPDF>({
    margem_superior: 20,
    margem_inferior: 20,
    margem_esquerda: 20,
    margem_direita: 20,
    fonte_padrao: 'Arial',
    tamanho_fonte: 12,
    incluir_cabecalho: false,
    incluir_rodape: true,
    watermark: ''
  })

  useEffect(() => {
    fetchConfiguracoes()
  }, [])

  const fetchConfiguracoes = async () => {
    try {
      const response = await fetch('/api/contratos/configuracoes')
      const data = await response.json()
      
      if (data.empresa) setConfigEmpresa(data.empresa)
      if (data.numeracao) setConfigNumeracao(data.numeracao)
      if (data.pdf) setConfigPDF(data.pdf)
    } catch (error) {
      console.error('Erro ao carregar configurações:', error)
    }
  }

  const handleSaveEmpresa = async () => {
    setSaving(true)
    try {
      const response = await fetch('/api/contratos/configuracoes', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tipo: 'empresa',
          dados: configEmpresa
        })
      })

      if (response.ok) {
        alert('Configurações da empresa salvas com sucesso!')
      } else {
        const result = await response.json()
        alert('Erro ao salvar: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error)
      alert('Erro ao salvar configurações')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveNumeracao = async () => {
    setSaving(true)
    try {
      const response = await fetch('/api/contratos/configuracoes', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tipo: 'numeracao',
          dados: configNumeracao
        })
      })

      if (response.ok) {
        alert('Configurações de numeração salvas com sucesso!')
      } else {
        const result = await response.json()
        alert('Erro ao salvar: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error)
      alert('Erro ao salvar configurações')
    } finally {
      setSaving(false)
    }
  }

  const handleSavePDF = async () => {
    setSaving(true)
    try {
      const response = await fetch('/api/contratos/configuracoes', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tipo: 'pdf',
          dados: configPDF
        })
      })

      if (response.ok) {
        alert('Configurações de PDF salvas com sucesso!')
      } else {
        const result = await response.json()
        alert('Erro ao salvar: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error)
      alert('Erro ao salvar configurações')
    } finally {
      setSaving(false)
    }
  }

  const handleResetNumeracao = async () => {
    if (!confirm('Tem certeza que deseja resetar a numeração? Isso pode causar conflitos com contratos existentes.')) {
      return
    }

    try {
      const response = await fetch('/api/contratos/configuracoes/reset-numeracao', {
        method: 'POST'
      })

      if (response.ok) {
        setConfigNumeracao(prev => ({ ...prev, proximo_numero: 1 }))
        alert('Numeração resetada com sucesso!')
      } else {
        const result = await response.json()
        alert('Erro ao resetar: ' + result.error)
      }
    } catch (error) {
      console.error('Erro ao resetar numeração:', error)
      alert('Erro ao resetar numeração')
    }
  }

  const sections = [
    { id: 'empresa', label: 'Dados da Empresa', icon: Building },
    { id: 'numeracao', label: 'Numeração', icon: Hash },
    { id: 'pdf', label: 'Configurações PDF', icon: FileText }
  ]

  return (
    <div className="space-y-6">
      {/* Navegação das Seções */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {sections.map((section) => {
            const Icon = section.icon
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeSection === section.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{section.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Seção: Dados da Empresa */}
      {activeSection === 'empresa' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Dados da Empresa</h3>
              <p className="text-sm text-gray-600">Informações que aparecerão nos contratos</p>
            </div>
            <button
              onClick={handleSaveEmpresa}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Razão Social *
              </label>
              <input
                type="text"
                value={configEmpresa.razao_social}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, razao_social: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome Fantasia *
              </label>
              <input
                type="text"
                value={configEmpresa.nome_fantasia}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, nome_fantasia: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CNPJ *
              </label>
              <input
                type="text"
                value={configEmpresa.cnpj}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, cnpj: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefone
              </label>
              <input
                type="text"
                value={configEmpresa.telefone}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, telefone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endereço Completo *
              </label>
              <input
                type="text"
                value={configEmpresa.endereco}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, endereco: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cidade *
              </label>
              <input
                type="text"
                value={configEmpresa.cidade}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, cidade: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                UF *
              </label>
              <input
                type="text"
                value={configEmpresa.uf}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, uf: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                maxLength={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CEP *
              </label>
              <input
                type="text"
                value={configEmpresa.cep}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, cep: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={configEmpresa.email}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Representante Legal *
              </label>
              <input
                type="text"
                value={configEmpresa.representante_legal}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, representante_legal: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cargo do Representante
              </label>
              <input
                type="text"
                value={configEmpresa.cargo_representante}
                onChange={(e) => setConfigEmpresa(prev => ({ ...prev, cargo_representante: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Seção: Numeração */}
      {activeSection === 'numeracao' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Configurações de Numeração</h3>
              <p className="text-sm text-gray-600">Como os contratos serão numerados automaticamente</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleResetNumeracao}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Resetar
              </button>
              <button
                onClick={handleSaveNumeracao}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Formato da Numeração
              </label>
              <input
                type="text"
                value={configNumeracao.formato}
                onChange={(e) => setConfigNumeracao(prev => ({ ...prev, formato: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Ex: M4-YYYY-XXX"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use YYYY para ano e XXX para número sequencial
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prefixo
              </label>
              <input
                type="text"
                value={configNumeracao.prefixo}
                onChange={(e) => setConfigNumeracao(prev => ({ ...prev, prefixo: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Ex: M4"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Próximo Número
              </label>
              <input
                type="number"
                value={configNumeracao.proximo_numero}
                onChange={(e) => setConfigNumeracao(prev => ({ ...prev, proximo_numero: parseInt(e.target.value) || 1 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ano Atual
              </label>
              <input
                type="number"
                value={configNumeracao.ano_atual}
                onChange={(e) => setConfigNumeracao(prev => ({ ...prev, ano_atual: parseInt(e.target.value) || new Date().getFullYear() }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="2020"
                max="2030"
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="reset_anual"
                  checked={configNumeracao.reset_anual}
                  onChange={(e) => setConfigNumeracao(prev => ({ ...prev, reset_anual: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="reset_anual" className="text-sm font-medium text-gray-700">
                  Resetar numeração automaticamente a cada ano
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Se ativado, a numeração volta para 001 no início de cada ano
              </p>
            </div>

            <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Preview da Numeração</h4>
              <p className="text-lg font-mono text-gray-700">
                {configNumeracao.formato
                  .replace('YYYY', configNumeracao.ano_atual.toString())
                  .replace('XXX', configNumeracao.proximo_numero.toString().padStart(3, '0'))
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Seção: Configurações PDF */}
      {activeSection === 'pdf' && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Configurações de PDF</h3>
              <p className="text-sm text-gray-600">Como os PDFs dos contratos serão gerados</p>
            </div>
            <button
              onClick={handleSavePDF}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Margem Superior (mm)
              </label>
              <input
                type="number"
                value={configPDF.margem_superior}
                onChange={(e) => setConfigPDF(prev => ({ ...prev, margem_superior: parseInt(e.target.value) || 20 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="10"
                max="50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Margem Inferior (mm)
              </label>
              <input
                type="number"
                value={configPDF.margem_inferior}
                onChange={(e) => setConfigPDF(prev => ({ ...prev, margem_inferior: parseInt(e.target.value) || 20 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="10"
                max="50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Margem Esquerda (mm)
              </label>
              <input
                type="number"
                value={configPDF.margem_esquerda}
                onChange={(e) => setConfigPDF(prev => ({ ...prev, margem_esquerda: parseInt(e.target.value) || 20 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="10"
                max="50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Margem Direita (mm)
              </label>
              <input
                type="number"
                value={configPDF.margem_direita}
                onChange={(e) => setConfigPDF(prev => ({ ...prev, margem_direita: parseInt(e.target.value) || 20 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="10"
                max="50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fonte Padrão
              </label>
              <select
                value={configPDF.fonte_padrao}
                onChange={(e) => setConfigPDF(prev => ({ ...prev, fonte_padrao: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="Arial">Arial</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Georgia">Georgia</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tamanho da Fonte
              </label>
              <input
                type="number"
                value={configPDF.tamanho_fonte}
                onChange={(e) => setConfigPDF(prev => ({ ...prev, tamanho_fonte: parseInt(e.target.value) || 12 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                min="8"
                max="16"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Watermark (opcional)
              </label>
              <input
                type="text"
                value={configPDF.watermark}
                onChange={(e) => setConfigPDF(prev => ({ ...prev, watermark: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Ex: CONFIDENCIAL"
              />
            </div>

            <div className="md:col-span-2 space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="incluir_cabecalho"
                  checked={configPDF.incluir_cabecalho}
                  onChange={(e) => setConfigPDF(prev => ({ ...prev, incluir_cabecalho: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="incluir_cabecalho" className="text-sm font-medium text-gray-700">
                  Incluir cabeçalho automático
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="incluir_rodape"
                  checked={configPDF.incluir_rodape}
                  onChange={(e) => setConfigPDF(prev => ({ ...prev, incluir_rodape: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="incluir_rodape" className="text-sm font-medium text-gray-700">
                  Incluir rodapé com numeração de páginas
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContratosConfiguracoes
